import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { apiCall } from '@/lib/api';

export interface MembershipInfo {
  organization_id: string;
  role: string;
  permissions: string[];
}

export interface PermissionsResp {
  email: string | null;
  user_id: string | null;
  is_super_admin: boolean;
  memberships: MembershipInfo[];
}

interface OrgInfo {
  id: string;
  name: string;
  slug: string;
}

interface CtxValue {
  loading: boolean;
  error: string | null;
  data: PermissionsResp | null;
  orgs: OrgInfo[];                                 // names looked up from /v1/orgs (super-admin) or single membership
  currentOrgId: string | null;
  setCurrentOrgId: (id: string | null) => void;
  has: (key: string) => boolean;
}

const Ctx = createContext<CtxValue | null>(null);

const STORAGE_KEY = 'msp:current_org_id';

export function PermissionsProvider({ children }: { children: React.ReactNode }) {
  const { getToken, isSignedIn } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<PermissionsResp | null>(null);
  const [orgs, setOrgs] = useState<OrgInfo[]>([]);
  const [currentOrgId, setCurrentOrgIdState] = useState<string | null>(() => localStorage.getItem(STORAGE_KEY));

  function setCurrentOrgId(id: string | null) {
    setCurrentOrgIdState(id);
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
  }

  useEffect(() => {
    if (!isSignedIn) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const token = await getToken();
        const perms = await apiCall<PermissionsResp>('/v1/me/permissions', { bearerToken: token });
        if (cancelled) return;
        setData(perms);

        // Look up org names. Super-admins can list every org; regular users
        // can only see the orgs they're members of, which we already know.
        let resolved: OrgInfo[] = [];
        if (perms.is_super_admin) {
          try {
            const all = await apiCall<{ items: OrgInfo[] }>('/v1/orgs', { bearerToken: token });
            resolved = all.items.map((o) => ({ id: o.id, name: o.name, slug: o.slug }));
          } catch { resolved = []; }
        } else {
          // For non-super-admins, the membership list has the org IDs but no
          // names. Best effort: each org we already see via members/payments
          // pages already surfaces its name, but the topbar needs it
          // up-front, so we fall back to the slug-ish id when unresolvable.
          for (const m of perms.memberships) {
            resolved.push({ id: m.organization_id, name: m.organization_id.slice(0, 8), slug: m.organization_id });
          }
        }
        if (cancelled) return;
        setOrgs(resolved);
        // Pick a default current org if the stored one is no longer valid.
        const stored = localStorage.getItem(STORAGE_KEY);
        const validIds = new Set(perms.is_super_admin ? resolved.map((o) => o.id) : perms.memberships.map((m) => m.organization_id));
        if (stored && validIds.has(stored)) {
          // keep
        } else if (perms.memberships.length > 0) {
          setCurrentOrgId(perms.memberships[0]!.organization_id);
        } else if (perms.is_super_admin && resolved.length > 0) {
          setCurrentOrgId(resolved[0]!.id);
        } else {
          setCurrentOrgId(null);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'permissions fetch failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isSignedIn, getToken]);

  const has = useMemo(() => {
    return (key: string): boolean => {
      if (!data) return false;
      if (data.is_super_admin) return true;
      const m = data.memberships.find((x) => x.organization_id === currentOrgId);
      if (m?.permissions.includes(key)) return true;
      // Fall back to ANY membership granting the perm (covers users with
      // exactly one membership where currentOrgId hasn't been set yet).
      return data.memberships.some((x) => x.permissions.includes(key));
    };
  }, [data, currentOrgId]);

  return (
    <Ctx.Provider value={{ loading, error, data, orgs, currentOrgId, setCurrentOrgId, has }}>
      {children}
    </Ctx.Provider>
  );
}

export function usePermissions(): CtxValue {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('usePermissions must be used inside <PermissionsProvider>');
  return ctx;
}

// Convenience hook — returns true when the caller has the named permission.
export function usePermission(key: string): boolean {
  return usePermissions().has(key);
}
